"use client";

import { useEffect, useRef, useState } from "react";

/**
 * The floating-panel plumbing the pickers share: an anchor ref, a panel ref,
 * open state, and where to put the panel — fixed and portaled to `body`, so
 * toolbars and table wrappers whose overflow would clip an absolutely
 * positioned child can't (the /logs filter bar clipped a whole calendar).
 * Re-places on scroll and resize; closes on outside click and Escape. The
 * panel's width keeps it inside the viewport when the anchor sits at the
 * right edge.
 */
export function usePopoverAnchor(panelWidth: number) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null,
  );

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      setCoords({
        top: r.bottom + 4,
        left: Math.max(8, Math.min(r.left, window.innerWidth - panelWidth - 8)),
      });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, panelWidth]);

  return { open, setOpen, ref, popRef, coords };
}

/** `YYYY-MM-DD` → a LOCAL Date (no UTC shift), or undefined. */
export function parseYmd(s: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/** Local Date → `YYYY-MM-DD` (matches `<input type="date">`'s value). */
export function fmtYmd(d: Date): string {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}
